import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ISubscription } from 'rxjs/Subscription';

import { CommonService } from '../../../core/common-service/common.service';
import { SecurityService } from '../../../core/security/security.service';
import { ReportsService } from '../../reports-service/reports.service';
import { Report_Param_DateRange_Responses, Report_Param_DateRange_Response, Parameter } from '../../reports-service/reports.model';
import { Switch } from '../../../shared/switch/switch.model';
import * as moment from 'moment';

@Component({
  selector: 'app-rpt-date-range',
  templateUrl: './rpt-date-range.component.html',
  styleUrls: ['./rpt-date-range.component.scss']
})
export class RptDateRangeComponent implements OnInit {

  @Input() parameter: Parameter;
  @Input() dateRanges: Report_Param_DateRange_Response[];
  @Input() fromDate: Date;
  @Input() toDate: Date;

  @Output() onError = new EventEmitter<boolean>();
  @Output() onFromDate = new EventEmitter<Date>();
  @Output() onToDate = new EventEmitter<Date>();

  showFiscalWeeks: Switch = new Switch("Filter By Fiscal Date", true, false);
  error: boolean;
  fromError: string = "";
  toError: string = "";
  dayRange: number = 0;
  fiscalWeekRange: number = 0;

  constructor(private _common: CommonService,
    private _security: SecurityService,
    private _reports: ReportsService) { }

  ngOnInit() {
    this.parseValidations();
  }

  private parseValidations() {
    const csvItems = this.parameter.validations.split(',');
    for (const part of csvItems) {
      const [key, value] = part.split(':').map(s => s.trim());
      const intValue = parseInt(value);
      const boolValue = intValue === 1;

      switch (key) {
        case 'DAYRANGE':
          if (!isNaN(intValue)) this.dayRange = intValue;
          break;
        case 'DEFAULTFISCALWEEK':
          this.showFiscalWeeks.input = boolValue;
          break;
        case 'FISCALWEEKRANGE':
          if (!isNaN(intValue)) this.fiscalWeekRange = intValue;
          break;
      }
    }
  }

  selectFromDate(fiscalWeek: Report_Param_DateRange_Response) {
    this.fromDate = new Date(fiscalWeek.weekStartDateString);
    this.toDate = new Date(fiscalWeek.weekEndDateString);
    this.onFromDate.emit(this.fromDate);
    this.onToDate.emit(this.toDate);
    this.validateCalendarDates();
    this.validateFiscalSelection();
  }

  selectToDate(fiscalWeek: Report_Param_DateRange_Response) {
    this.toDate = new Date(fiscalWeek.weekEndDateString);
    this.onToDate.emit(this.toDate);
    this.validateCalendarDates();
    this.validateFiscalSelection();
  }

  showFromError() {
    return this.fromError;
  }

  showToError() {
    return this.toError;
  }

  toggleDatePickers(element, other) {
    element.toggle();
    other.close();
  }

  update(date, isFrom: boolean) {
    this.clearErrors();
    
    if (!moment(date).isValid()) {
      this.setError(isFrom ? "This field is required." : "This field is required.", isFrom);
      return;
    }

    if (isFrom) {
      this.fromDate = date;
      this.onFromDate.emit(date);
    } else {
      this.toDate = date;
      this.onToDate.emit(date);
    }

    this.validateCalendarDates();
  }

  private clearErrors() {
    this.fromError = "";
    this.toError = "";
    this.error = false;
  }

  private setError(message: string, isFromError: boolean) {
    this.error = true;
    if (isFromError) this.fromError = message;
    else this.toError = message;
    this.onError.emit(this.error);
  }

  private validateCalendarDates() {
    if (this.fromDate > this.toDate) {
      this.setError("From cannot be after To", true);
      return;
    }

    if (this.dayRange > 0) {
      const daysDiff = moment(this.toDate).add(1, 'days').diff(moment(this.fromDate), 'days');
      if (daysDiff > this.dayRange) {
        this.setError(`Days cannot be more than ${this.dayRange} days apart`, true);
        return;
      }
    }

    this.onError.emit(false);
  }

  private validateFiscalSelection() {
    this.clearErrors();
    
    if (this.fiscalWeekRange > 0) {
      const fromWeek = this.findFiscalWeekByDate(this.fromDate);
      const toWeek = this.findFiscalWeekByDate(this.toDate);
      // Because 1 fiscal week is technically the same fiscal week, the index will start at 0
      const validateWeekDiff = Math.abs(fromWeek.id - toWeek.id) > this.fiscalWeekRange - 1;      
      if (fromWeek && toWeek && validateWeekDiff) {
        this.setError(`This report is limited to ${this.fiscalWeekRange} fiscal week${this.fiscalWeekRange > 1 ? 's' : ''}`, true);
        return;
      }
    }

    this.onError.emit(false);
  }

  private findFiscalWeekByDate(date: Date): Report_Param_DateRange_Response {
    if (!this.dateRanges) return null;
    return this.dateRanges.find(week => {
      const weekStart = new Date(week.weekStartDateString);
      const weekEnd = new Date(week.weekEndDateString);
      return date >= weekStart && date <= weekEnd;
    });
  }
  getToDates(): Report_Param_DateRange_Response[] {
    if (!this.fromDate) 
      return this.dateRanges;
    
    const fromWeek = this.findFiscalWeekByDate(this.fromDate);
    return fromWeek ? this.dateRanges.filter(week => fromWeek.id - week.id < 1) : this.dateRanges || [];
  }

}
